// src/components/SmartFlow/NodePalette.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { nodeRegistry } from "./nodes";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/**
 * Normaliza qualquer valor de icon para Bootstrap Icons.
 * Aceita:
 *  - "bi-globe"
 *  - "globe"
 *  - "bi bi-globe"
 *  - null/undefined/""
 * Retorna SEMPRE: "bi bi-<name>"
 */
const normalizeBootstrapIcon = (icon) => {
  const raw = String(icon ?? "").trim();

  // Sem ícone -> default
  if (!raw) return "bi bi-box-seam";

  // Já veio completo: "bi bi-xxx"
  if (raw.startsWith("bi bi-")) return raw;

  // Veio "bi-xxx"
  if (raw.startsWith("bi-")) return `bi ${raw}`;

  // Veio "bi xxx" ou coisa parecida
  if (raw.startsWith("bi ")) {
    const parts = raw.split(/\s+/).filter(Boolean);
    // tenta achar o token que começa com "bi-"
    const biToken = parts.find((p) => p.startsWith("bi-"));
    return biToken ? `bi ${biToken}` : "bi bi-box-seam";
  }

  // Veio só o nome "globe" -> vira "bi bi-globe"
  const safeName = raw.replace(/^icon-/, "").replace(/\s+/g, "-");
  return `bi bi-${safeName}`;
};

const NodePalette = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [nodeTypes, setNodeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Resizable width
  const DEFAULT_WIDTH = 280;
  const MIN_WIDTH = 220;
  const MAX_WIDTH = 520;

  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem("smartflow.nodePalette.width"));
    return Number.isFinite(saved) && saved > 0 ? clamp(saved, MIN_WIDTH, MAX_WIDTH) : DEFAULT_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWRef = useRef(panelWidth);

  // ✅ DOM-first resize (profissional): evita loop do ResizeObserver
  const panelRef = useRef(null);
  const pendingWidthRef = useRef(panelWidth);
  const rafIdRef = useRef(null);

  const getCategoryLabel = (category) => {
    const labelMap = {
      trigger: "Triggers",
      action: "Actions",
      condition: "Logic",
      utility: "Utilities",
    };
    return labelMap[category] || "Other";
  };

  useEffect(() => {
    const fetchNodes = async () => {
      try {
        setLoading(true);

        if (!nodeRegistry.isReady()) {
          await nodeRegistry.initialize();
        }

        const nodesMetadata = nodeRegistry.getAll();

        const nodes = nodesMetadata.map((node) => ({
          type: node.type,
          label: node.name,
          icon: normalizeBootstrapIcon(node.icon),
          color: node.color || "#3498db",
          description: node.description || "",
          category: getCategoryLabel(node.category),
          config_schema: node.configSchema || {},
          required_integration: node.requiredIntegration,
        }));

        setNodeTypes(nodes);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch nodes:", err);
        setError("Failed to load nodes. Please try again.");
        setNodeTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("smartflow.nodePalette.width", String(panelWidth));
  }, [panelWidth]);

  // mantém o DOM alinhado quando o state mudar (ex: reload, storage)
  useEffect(() => {
    pendingWidthRef.current = panelWidth;
    if (panelRef.current) {
      panelRef.current.style.width = `${panelWidth}px`;
      // se estiver dentro de flex, isso é ainda mais estável:
      panelRef.current.style.flex = `0 0 ${panelWidth}px`;
    }
  }, [panelWidth]);

  // limpa RAF ao desmontar
  useEffect(() => {
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const applyDomWidth = (w) => {
      if (!panelRef.current) return;
      panelRef.current.style.width = `${w}px`;
      panelRef.current.style.flex = `0 0 ${w}px`;
    };

    const scheduleDomWidth = (next) => {
      pendingWidthRef.current = next;

      if (rafIdRef.current) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        applyDomWidth(pendingWidthRef.current);
      });
    };

    const onMove = (e) => {
      const delta = e.clientX - resizeStartXRef.current;
      const next = clamp(resizeStartWRef.current + delta, MIN_WIDTH, MAX_WIDTH);
      scheduleDomWidth(next);
    };

    const onUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      // garante aplicar o último valor no DOM
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      applyDomWidth(pendingWidthRef.current);

      // ✅ commita no React state só 1x (fim do drag)
      setPanelWidth(pendingWidthRef.current);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    };
  }, [isResizing]);

  const beginResize = (e) => {
    setIsResizing(true);
    resizeStartXRef.current = e.clientX;
    resizeStartWRef.current = pendingWidthRef.current; // usa o atual real
    pendingWidthRef.current = pendingWidthRef.current;

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(nodeTypes.map((node) => node.category))];
    return ["All", ...uniqueCategories.sort()];
  }, [nodeTypes]);

  const filteredNodes = useMemo(() => {
    let filtered = nodeTypes;

    if (selectedCategory !== "All") {
      filtered = filtered.filter((node) => node.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (node) =>
          node.label.toLowerCase().includes(query) ||
          node.description.toLowerCase().includes(query) ||
          node.type.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [searchQuery, selectedCategory, nodeTypes]);

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const styles = {
    panel: {
      width: panelWidth,
      minWidth: MIN_WIDTH,
      maxWidth: MAX_WIDTH,
      background: "white",
      borderRight: "1px solid #e6e6e6",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      position: "relative",

      // ✅ mais estável em layouts flex (canvas + sidebar)
      flex: `0 0 ${panelWidth}px`,
    },
    header: {
      padding: "16px 16px 10px 16px",
      borderBottom: "1px solid #f0f0f0",
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
    },
    title: {
      margin: 0,
      fontSize: 14,
      fontWeight: 700,
      color: "#2c3e50",
    },
    sub: {
      margin: "6px 0 0 0",
      fontSize: 12,
      color: "#7f8c8d",
    },
    body: {
      padding: 16,
      overflow: "auto",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      flex: 1,
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #e1e1e1",
      borderRadius: 10,
      fontSize: 13,
      outline: "none",
    },
    tabs: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
    },
    tabBtn: (active) => ({
      padding: "6px 10px",
      fontSize: 12,
      border: "1px solid " + (active ? "#3498db" : "#e1e1e1"),
      borderRadius: 999,
      background: active ? "#3498db" : "white",
      color: active ? "white" : "#566573",
      cursor: "pointer",
      transition: "all 0.15s",
      whiteSpace: "nowrap",
    }),
    list: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      flex: 1,
      paddingBottom: 8,
    },
    card: {
      padding: 12,
      background: "#fbfbfb",
      borderRadius: 12,
      cursor: "grab",
      transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
      boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
      border: "1px solid #eee",
      userSelect: "none",
    },
    cardTop: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 6,
      minWidth: 0,
    },
    icon: (color) => ({
      fontSize: 18,
      lineHeight: "18px",
      color: color,
      width: 22,
      display: "inline-flex",
      justifyContent: "center",
    }),
    label: {
      fontWeight: 600,
      fontSize: 13,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    desc: {
      fontSize: 12,
      color: "#7f8c8d",
      marginLeft: 32,
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
    },
    resizeHandle: {
      position: "absolute",
      top: 0,
      right: 0,
      height: "100%",
      width: 10,
      cursor: "col-resize",
    },
    resizeBar: {
      position: "absolute",
      top: 0,
      right: 3,
      height: "100%",
      width: 2,
      background: isResizing ? "#3498db" : "transparent",
      transition: "background 0.12s ease",
    },
  };

  return (
    <div ref={panelRef} style={styles.panel} aria-label="Node palette">
      <div style={styles.resizeHandle} onMouseDown={beginResize} title="Drag to resize" aria-hidden="true">
        <div style={styles.resizeBar} />
      </div>

      <div style={styles.header}>
        <div style={styles.titleRow}>
          <h3 style={styles.title}>Node Palette</h3>
          <span style={{ fontSize: 11, color: "#95a5a6" }}>
            {filteredNodes.length}/{nodeTypes.length}
          </span>
        </div>
        <p style={styles.sub}>Drag nodes to the canvas</p>

        <input
          type="text"
          placeholder="Search nodes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.input}
          onFocus={(e) => (e.currentTarget.style.borderColor = "#3498db")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "#e1e1e1")}
        />

        <div style={{ marginTop: 10 }}>
          <div style={styles.tabs}>
            {categories.map((category) => {
              const active = selectedCategory === category;
              return (
                <button key={category} onClick={() => setSelectedCategory(category)} style={styles.tabBtn(active)}>
                  {category}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={styles.body}>
        {loading && (
          <div style={{ padding: "30px 10px", textAlign: "center", color: "#7f8c8d", fontSize: 13 }}>
            <div style={{ marginBottom: 10 }}>
              <i className="bi bi-hourglass-split" aria-hidden="true" />
            </div>
            Loading nodes...
          </div>
        )}

        {error && !loading && (
          <div
            style={{
              padding: 12,
              background: "#fff0f0",
              border: "1px solid #ffd0d0",
              borderRadius: 12,
              color: "#c0392b",
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <div style={styles.list}>
            {filteredNodes.length > 0 ? (
              filteredNodes.map((node) => (
                <div
                  key={node.type}
                  draggable
                  onDragStart={(e) => onDragStart(e, node.type)}
                  style={{
                    ...styles.card,
                    border: `1px solid ${node.color}33`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)";
                    e.currentTarget.style.background = node.color + "10";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 1px 0 rgba(0,0,0,0.03)";
                    e.currentTarget.style.background = "#fbfbfb";
                  }}
                >
                  <div style={styles.cardTop}>
                    <i className={node.icon} style={styles.icon(node.color)} aria-hidden="true" />
                    <span style={{ ...styles.label, color: node.color }}>{node.label}</span>
                  </div>
                  <div style={styles.desc}>{node.description || "—"}</div>
                </div>
              ))
            ) : (
              <div style={{ padding: "30px 10px", textAlign: "center", color: "#7f8c8d", fontSize: 13 }}>
                No nodes found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NodePalette;
